import React, { useCallback, useRef, useState } from "react";
import { Pressable, View, type ViewStyle } from "react-native";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react";
import { AnimatePresence, MotiView } from "moti";
import { CaretDownIcon, CheckIcon, SpeakerSimpleHighIcon } from "@phosphor-icons/react";
import { Portal } from "react-native-paper";

import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import { colors, spacing } from "../../../../styles/design-system";
import { type PlaybackSpeed } from "../../../../utils/toolPreferenceStorage";
import CustomText from "../../../common/general/CustomText/CustomText";
import { ToggleMenuRow, styles } from "./ToolsMenu";

export type VoiceMenuProps = {
  isDisabled: boolean;
  isAudioOn: boolean;
  onAudioOnPress: () => void;
  onAudioOffPress: () => void;
  playbackSpeed: PlaybackSpeed;
  onPlaybackSpeedChange: (speed: PlaybackSpeed) => void;
};

const PLAYBACK_SPEEDS: PlaybackSpeed[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

export const VoiceMenu: React.FC<VoiceMenuProps> = ({
  isDisabled,
  isAudioOn,
  onAudioOnPress,
  onAudioOffPress,
  playbackSpeed,
  onPlaybackSpeedChange,
}) => {
  const [isVoiceMenuOpen, setIsVoiceMenuOpen] = useState(false);

  const { refs, x, y, strategy, placement } = useFloating({
    placement: "top-start",
    middleware: [offset(spacing.xs), flip(), shift({ padding: spacing.sm })],
    whileElementsMounted: autoUpdate,
  });

  const closeVoiceMenu = useCallback(() => {
    setIsVoiceMenuOpen(false);
  }, []);

  const floatingTrapRef = useRef<View | null>(null);
  useFocusTrap(floatingTrapRef, { visible: isVoiceMenuOpen, onEscape: closeVoiceMenu });

  const handleVoiceMenuToggle = useCallback(() => {
    if (isVoiceMenuOpen) {
      closeVoiceMenu();
      return;
    }
    setIsVoiceMenuOpen(true);
  }, [closeVoiceMenu, isVoiceMenuOpen]);

  const handleAutoReadAloudChange = (enabled: boolean) => {
    if (enabled) {
      onAudioOnPress();
      return;
    }

    onAudioOffPress();
  };

  const isVoiceTriggerDisabled = isDisabled && !isVoiceMenuOpen;
  const openUpward = placement.startsWith("top");

  return (
    <View style={styles.toolsMenuContainer} ref={refs.setReference as unknown as React.Ref<View>}>
      <Pressable
        onPress={handleVoiceMenuToggle}
        disabled={isVoiceTriggerDisabled}
        role="button"
        accessibilityLabel={isVoiceMenuOpen ? "Close voice menu" : "Open voice menu"}
        style={(state) => {
          const hovered = (state as { hovered?: boolean }).hovered;
          return [
            styles.toolsTriggerButton,
            !isVoiceTriggerDisabled && hovered && styles.toolsTriggerButtonHovered,
            !isVoiceTriggerDisabled && state.pressed && styles.toolsTriggerButtonPressed,
            isVoiceTriggerDisabled && styles.iconButtonDisabled,
          ];
        }}>
        <SpeakerSimpleHighIcon size={16} color={colors.grey[600]} weight="regular" />
        <CustomText text="Voice" useTranslationText={false} style={styles.toolsTriggerText} />
        <CaretDownIcon size={12} color={colors.grey[400]} weight="bold" />
      </Pressable>

      <Portal>
        {isVoiceMenuOpen ? (
          <Pressable style={styles.toolsMenuOverlay} onPress={closeVoiceMenu} accessibilityLabel="Close voice menu" />
        ) : null}
        <AnimatePresence>
          {isVoiceMenuOpen ? (
            <View
              ref={(node) => {
                (refs.setFloating as unknown as (node: View | null) => void)(node);
                floatingTrapRef.current = node;
              }}
              role="dialog"
              aria-label="Voice menu"
              accessibilityRole="none"
              accessibilityLabel="Voice menu"
              style={[
                styles.toolsMenuPosition,
                {
                  position: (strategy === "fixed" ? "absolute" : strategy) as ViewStyle["position"],
                  left: x ?? 0,
                  top: y ?? 0,
                },
              ]}>
              <MotiView
                key="voice-menu"
                style={styles.toolsMenu}
                from={{
                  opacity: 0,
                  translateY: openUpward ? 8 : -8,
                  scale: 0.98,
                }}
                animate={{ opacity: 1, translateY: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  translateY: openUpward ? 8 : -8,
                  scale: 0.98,
                }}
                transition={{ type: "timing", duration: 180 }}>
                <View style={styles.menuSection}>
                  <ToggleMenuRow
                    label="Auto Read Aloud"
                    value={isAudioOn}
                    onToggle={handleAutoReadAloudChange}
                    accessibilityLabel="Auto read aloud toggle"
                  />
                </View>

                <View style={styles.menuSeparator} />

                <View style={styles.menuSection}>
                  <View style={styles.menuSectionRowHeader}>
                    <CustomText text="Playback Speed" useTranslationText={false} style={styles.menuSectionHeaderText} />
                  </View>

                  <View style={styles.toolsGrid}>
                    {PLAYBACK_SPEEDS.map((speed) => {
                      const isActiveSpeed = playbackSpeed === speed;

                      return (
                        <Pressable
                          key={speed}
                          role="button"
                          onPress={() => onPlaybackSpeedChange(speed)}
                          style={(state) => {
                            const hovered = (state as { hovered?: boolean }).hovered;
                            return [
                              styles.toolMenuItem,
                              isActiveSpeed && styles.toolMenuItemActive,
                              !isActiveSpeed && hovered && styles.menuItemHovered,
                              state.pressed && styles.menuItemPressed,
                            ];
                          }}>
                          <CustomText
                            text={`${speed}x`}
                            useTranslationText={false}
                            style={[styles.toolMenuItemText, isActiveSpeed && styles.toolMenuItemTextActive]}
                          />
                          {isActiveSpeed ? <CheckIcon size={14} color={colors.brand[600]} weight="bold" /> : null}
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              </MotiView>
            </View>
          ) : null}
        </AnimatePresence>
      </Portal>
    </View>
  );
};
